// A built kit's walls (docs/164 ET5 and "The keep's look", docs/186 EA3): each wall square drawn
// `wall_rise` px above its square with its front wherever the square below draws no wall, and cut into
// one standing image per square so that it sorts with the units: a figure behind a wall is hidden by it.
// Ported from `draw_walls` in `tools/encounter-art.py`; every setting is the kit's `look.json`.
import type { ArtLight, Piece, Standing } from '../types';
import { Canvas, hex, sameRGB, type RGBA } from './canvas';
import { scaleLightness } from './colour';
import { C, type Ctx } from './context';

/** A piece list by name: the set's own, or a coat's (`coated`). */
type Get = (piece: string) => readonly Piece[];

/** The 24-row front `rise` rows taller, from its own courses (look.json `tall_front`), so the bond runs on. */
export function tallFront(face: Piece, look: Ctx['look']): Piece {
  const rise = look.wall_rise ?? 0;
  const t = look.tall_front;
  if (!rise || !t) return face;
  const foot = [...(t.foot_rows ?? [])];
  const rows: number[] = [...t.cap_rows];
  for (let n = 0; n < 99 && rows.length < C + rise; n++) for (const [a, b] of t.courses) for (let r = a; r < b; r++) rows.push(r);
  const all = [...rows.slice(0, C + rise - foot.length), ...foot].slice(0, C + rise);
  const px = new Uint8ClampedArray(C * (C + rise) * 4);
  all.forEach((r, i) => px.set(face.px.subarray(r * face.w * 4, r * face.w * 4 + C * 4), i * C * 4));
  return { ...face, h: C + rise, px };
}

/** A piece set in a front (a door, a crack), raised with its wall: its own pixels (those that differ from the front it was laid on) at the tall front's foot. */
function raised(tile: Piece, base: Piece | undefined, front: Piece, box: boolean): Piece {
  const px = front.px.slice(), dy = front.h - C;
  const diff: Array<[number, number]> = [];
  for (let y = 0; y < C; y++) for (let x = 0; x < C; x++) {
    const i = (y * tile.w + x) * 4;
    if (!base || base.w < C || base.h < C) { if (tile.px[i + 3]) diff.push([x, y]); continue; }
    const j = (y * base.w + x) * 4;
    if (tile.px[i] !== base.px[j] || tile.px[i + 1] !== base.px[j + 1] || tile.px[i + 2] !== base.px[j + 2] || tile.px[i + 3] !== base.px[j + 3]) diff.push([x, y]);
  }
  let cells = diff;
  if (box && diff.length) {
    // look.json `set_in_box`: the piece's whole box, so a frame drawn in the front's own colours is kept (the house)
    const x0 = Math.min(...diff.map((d) => d[0])), x1 = Math.max(...diff.map((d) => d[0])), y0 = Math.min(...diff.map((d) => d[1]));
    cells = [];
    for (let y = y0; y < C; y++) for (let x = x0; x <= x1; x++) cells.push([x, y]);
  }
  for (const [x, y] of cells) px.set(tile.px.subarray((y * tile.w + x) * 4, (y * tile.w + x) * 4 + 4), ((y + dy) * C + x) * 4);
  return { ...front, px };
}

/** Turn a square piece a quarter (look.json `top_along`: a grained top runs along a wall that runs up the board). */
function quarter(p: Piece): Piece {
  const px = new Uint8ClampedArray(p.px.length);
  for (let y = 0; y < p.h; y++) for (let x = 0; x < p.w; x++) {
    // Pillow's ROTATE_90 (counter-clockwise): out(x, y) = in(w - 1 - y, x)
    const sx = p.w - 1 - y, sy = x;
    px.set(p.px.subarray((sy * p.w + sx) * 4, (sy * p.w + sx) * 4 + 4), (y * p.w + x) * 4);
  }
  return { ...p, px };
}

/** The set as a run in `coat` sees it (look.json `coats`, the docks and towns): its fronts are the coat's, and what is set in the plain front is laid by its own pixels on the coat's. */
function coated(get: Get, coat: string): Get {
  if (!coat) return get;
  const cache = new Map<string, readonly Piece[]>();
  return (piece) => {
    if (piece === 'wall_face') return get(`wall_face_${coat}`).length ? get(`wall_face_${coat}`) : get('wall_face');
    if (!/^(door_|arch_|hidden_way_|wall_cracked|window_front)/.test(piece)) return get(piece);
    const hit = cache.get(piece);
    if (hit) return hit;
    const plain = get('wall_face')[0], own = get(`wall_face_${coat}`)[0];
    const res = !plain || !own ? get(piece) : get(piece).map((im) => {
      const px = own.px.slice();
      for (let i = 0; i < C * C * 4; i += 4) {
        if (im.px[i] !== plain.px[i] || im.px[i + 1] !== plain.px[i + 1] || im.px[i + 2] !== plain.px[i + 2] || im.px[i + 3] !== plain.px[i + 3]) px.set(im.px.subarray(i, i + 4), i);
      }
      return { ...im, px };
    });
    cache.set(piece, res);
    return res;
  };
}

/** A run's squares' bounding box, inclusive. */
interface Box { x0: number; y0: number; x1: number; y1: number }

export interface WallsOut { standing: Standing[]; lights: ArtLight[]; headroom: number }

/**
 * Every wall square of a built kit as a standing image, plus what stands on the walls (chimneys, merlons),
 * and the soft shadow each wall throws on the floor square below it (into `ground`).
 */
export function paintWalls(ctx: Ctx, ground: Canvas): WallsOut {
  const { sq, look } = ctx;
  const { w, h } = sq;
  const rise = look.wall_rise ?? 0;
  const cren = look.battlements;
  const head = Math.max(0, cren?.n_rise ?? 0);
  const rowH = C + rise + head;
  const rows = new Map<number, RowCells>();
  const rowOf = (y: number): RowCells => { let c = rows.get(y); if (!c) { c = new RowCells(rowH); rows.set(y, c); } return c; };
  /** Board px of a row canvas's top. */
  const rowTop = (y: number): number => y * C - rise - head;
  const out: WallsOut = { standing: [], lights: [], headroom: rise + head };

  const wallAt = (x: number, y: number): boolean => sq.isWall(x, y);
  const isw = (x: number, y: number): boolean => sq.solid(x, y);
  const rock = (x: number, y: number): boolean => look.rock_is_top ? false : sq.at(x, y) === 'rock';
  // a lone wall in a room is a pillar, drawn as a stand (docs/186 EA6)
  const pillar = (x: number, y: number): boolean => ctx.has('stand') && sq.at(x, y) === 'wall' && !wallAt(x - 1, y) && !wallAt(x + 1, y) && !wallAt(x, y - 1) && !wallAt(x, y + 1);
  const doorAt = (x: number, y: number): boolean => { const s = sq.at(x, y); return (s === 'door' || s === 'way') && !(isw(x, y - 1) && isw(x, y + 1)); };

  const draw = (member: (x: number, y: number) => boolean, get: Get, box: Box = { x0: 0, y0: 0, x1: w - 1, y1: h - 1 }): void => {
    // every loop keeps to the run's own box (a coat run is a few squares of a big board)
    const { x0: X0, y0: Y0, x1: X1, y1: Y1 } = box;
    const inRun = (x: number, y: number): boolean => member(x, y) && wallAt(x, y) && !rock(x, y) && !pillar(x, y);
    const door = (x: number, y: number): boolean => member(x, y) && doorAt(x, y);
    const drawn = (x: number, y: number): boolean => inRun(x, y) || door(x, y);
    const faces = get('wall_face'), tops = get('wall_top');
    if (!faces.length || !tops.length) return;
    const pickOf = (vs: readonly Piece[], purpose: string, x: number, y: number, weights?: Ctx['look']['wall_face_weights']): Piece => {
      const r = ctx.roll(purpose, x, y);
      if (weights) {
        const ws = vs.map((p, i) => (Array.isArray(weights) ? weights[i] ?? 1 : (weights as Record<string, number>)[`${p.piece}_${String(p.v).padStart(2, '0')}`] ?? 1));
        const total = ws.reduce((a, b) => a + b, 0);
        if (total > 0) { let t = r % total; for (let i = 0; i < vs.length; i++) { t -= ws[i]!; if (t < 0) return vs[i]!; } }
      }
      return vs[r % vs.length]!;
    };
    const faceBase = look.dress_on_plain ? faces[0] : ctx.all('_face_base')[0] ?? faces[0];
    const dress = (look.wall_dress ?? ['wall_lit', 'wall_face_banner', 'wall_face_shield', 'wall_face_alcove']).map((p) => [p, get(p)] as const).filter(([, v]) => v.length);
    const every = look.wall_dress_every ?? 9;
    const edge = look.wall_edge;
    const darker = new Map<number, RGBA>();
    const dark = (c: RGBA): RGBA => {
      const k = (c[0] << 16) | (c[1] << 8) | c[2];
      let d = darker.get(k);
      if (!d) { d = ctx.snap(scaleLightness(c, edge?.lightness ?? 0.75)); darker.set(k, d); }
      return [d[0], d[1], d[2], c[3]];
    };
    const slopeTops = (x: number, y: number): readonly Piece[] => {
      if (!look.roof_slopes || !(drawn(x - 1, y) || drawn(x + 1, y))) return tops;
      let n = 0, s = 0;
      while (y - n - 1 >= 0 && drawn(x, y - n - 1)) n++;
      while (y + s + 1 < h && drawn(x, y + s + 1)) s++;
      const ridge = Math.floor((n + s) / 2);
      const back = get('wall_top_back'), ridgeP = get('wall_top_ridge');
      return n < ridge ? (back.length ? back : tops) : n === ridge ? (ridgeP.length ? ridgeP : tops) : tops;
    };
    const doorPiece = (x: number, y: number): readonly Piece[] => {
      const t = sq.tile(x, y);
      if (sq.at(x, y) === 'way') return get('hidden_way_front');
      const open = get('door_open_front');
      return t?.doorOpen && open.length ? open : get('door_closed_front');
    };

    for (let y = Y0; y <= Y1; y++) {
      const row = rowOf(y);
      for (let x = X0; x <= X1; x++) {
        if (!drawn(x, y)) continue;
        const south = !drawn(x, y + 1) || (rise > 0 && y + 1 >= h);
        // in the row canvas: the square's column, `head` rows down from its top
        const X = x * C, Y = head;
        const r0 = ctx.roll('dress', x, y) % every;
        const facesRoom = south && !door(x, y) && dress.length > 0 && sq.on(x, y + 1) && !isw(x, y + 1);
        const dressed = facesRoom && r0 < dress.length;
        const face = tallFront(look.dress_on_plain && (dressed || door(x, y)) ? faces[0]! : pickOf(faces, 'fa', x, y, look.wall_face_weights), look);
        let im: Piece;
        if (door(x, y)) {
          const d = doorPiece(x, y);
          im = d.length ? raised(pickOf(d, 'door', x, y), faceBase, face, !!look.set_in_box) : face;
        } else {
          im = south ? face : pickOf(slopeTops(x, y), 't', x, y, look.wall_top_weights);
          if (!south && look.top_along && (drawn(x, y - 1) || drawn(x, y + 1)) && !(drawn(x - 1, y) || drawn(x + 1, y))) im = quarter(im);
        }
        row.draw(im, X, Y);
        const H = im.h;
        const fo = look.wall_foot;
        if (fo && south && !door(x, y) && get(fo.piece).length) {
          // a strip along every front's foot (the cistern's tide line): its wash colour shades, its other colours lie
          const wash = hex(fo.wash), ft = pickOf(get(fo.piece), 'ft', x, y);
          for (let j = 0; j < Math.min(8, ft.h); j++) for (let i = 0; i < C; i++) {
            const c = Canvas.sample(ft, i, j);
            if (!c[3]) continue;
            const u = X + i, v = Y + H - Math.min(8, ft.h) + j;
            if (sameRGB(c, wash)) {
              const p = row.get(u, v), a = fo.alpha / 255;
              row.set(u, v, [p[0] + (wash[0] - p[0]) * a, p[1] + (wash[1] - p[1]) * a, p[2] + (wash[2] - p[2]) * a, 255]);
            } else row.set(u, v, c);
          }
        }
        const trim = look.wall_trim;
        if (trim) {
          // timber along the wall's outer edges: a plastered top shows its frame only where the wall ends (the house)
          trim.colours.forEach((col, k) => {
            const c = hex(col);
            for (let i = 0; i < C; i++) if (!south && !drawn(x, y - 1)) row.set(X + i, Y + k, c);
            for (let i = 0; i < (south ? H : C); i++) {
              if (!drawn(x - 1, y) && !south) row.set(X + k, Y + i, c);
              if (!drawn(x + 1, y) && !(south && trim.tops_only)) row.set(X + C - 1 - k, Y + i, c);
            }
          });
        }
        if (edge && !(edge.outer && !south)) {
          const open = (a: number, b: number): boolean => sq.on(a, b) && !isw(a, b) && !doorAt(a, b);
          for (let i = 0; i < C; i++) {
            if (open(x, y - 1)) row.set(X + i, Y, dark(row.get(X + i, Y)));
            if (south) row.set(X + i, Y + H - 1, dark(row.get(X + i, Y + H - 1)));
          }
          for (let i = 0; i < H; i++) {
            if (open(x - 1, y)) row.set(X, Y + i, dark(row.get(X, Y + i)));
            if (open(x + 1, y)) row.set(X + C - 1, Y + i, dark(row.get(X + C - 1, Y + i)));
          }
        }
        const corner = look.front_corner;
        if (corner && south) {
          // a timber post where a front ends (the town); its rows run from the beam to the sole
          const cols = corner.colours.map((c) => hex(c));
          for (let j = corner.head; j < H - corner.foot; j++) cols.forEach((c, i) => {
            if (!drawn(x - 1, y)) row.set(X + i, Y + j, c);
            if (!drawn(x + 1, y) && x + 1 < w) row.set(X + C - cols.length + i, Y + j, c);
          });
        }
        if (facesRoom && r0 < dress.length && !(r0 === 0 && ctx.roll('torch', x, y) % 2)) {
          const [p, ims] = dress[r0]!;
          const dy = rise + (look.wall_dressing_y ?? 0);
          row.draw(pickOf(ims, p, x, y), X, Y + dy);
          if (p === 'wall_lit') out.lights.push({ x: X + C / 2, y: rowTop(y) + Y + dy + 8, kind: 'torch' });
        }
      }
    }

    // the tops' whole outline in one colour, wherever a top meets anything else (the house): traced on the
    // board as drawn, so a front drawn later over a top's foot takes that part of the top away
    if (edge?.outer && edge.outer_colour) {
      const line = hex(edge.outer_colour), across = hex(edge.outer_colour_across ?? edge.outer_colour);
      // which row shows a top at each board px (0: none), over the board and the rows that rise above it
      const AX = X0 * C, BY = Y0 * C - rowH, BW = (X1 - X0 + 1) * C, BH = (Y1 - Y0 + 1) * C + rowH, top = new Int32Array(BW * BH);
      const key = (a: number, b: number): number => (b - BY) * BW + (a - AX);
      for (let y = Y0; y <= Y1; y++) for (let x = X0; x <= X1; x++) {
        if (!drawn(x, y)) continue;
        const south = !drawn(x, y + 1) || (rise > 0 && y + 1 >= h);
        const H = south ? C + rise : C, isTop = !south && !door(x, y);
        for (let j = 0; j < H; j++) {
          const k0 = key(x * C, rowTop(y) + head + j);
          top.fill(isTop ? y + 1 : 0, k0, k0 + C);
        }
      }
      const has = (a: number, b: number): boolean => a >= AX && a < AX + BW && b >= BY && b < BY + BH && top[key(a, b)]! > 0;
      for (let b = BY; b < BY + BH; b++) for (let a = AX; a < AX + BW; a++) {
        const y1 = top[key(a, b)]!;
        if (!y1) continue;
        const y = y1 - 1, row = rowOf(y), ry = b - rowTop(y);
        if (!has(a + 1, b) || !has(a - 1, b)) row.set(a, ry, line);
        else if (!has(a, b + 1) || !has(a, b - 1)) row.set(a, ry, across);
      }
    }

    // the soft shadow a wall throws on the floor square below it
    for (let y = Y0; y <= Math.min(h - 1, Y1 + 1); y++) for (let x = X0; x <= X1; x++) {
      if (!isw(x, y) && sq.at(x, y) !== 'void' && drawn(x, y - 1) && !door(x, y)) ground.rect(x * C, y * C, x * C + C - 1, y * C + 4, [0, 0, 0, 90]);
    }

    // what stands on the walls, drawn after them: chimneys on a roof's slope, merlons on a walk's open edges
    const after = (y: number): number => (y + 1) * C + C / 2 + 0.01;
    const chimneys = get('wall_top_chimney');
    if (chimneys.length) {
      const placed: Array<[number, number]> = [];
      for (let y = Math.max(1, Y0); y <= Math.min(h - 2, Y1); y++) for (let x = Math.max(1, X0); x <= Math.min(w - 2, X1); x++) {
        if (placed.some(([a, b]) => Math.abs(x - a) <= 2 && Math.abs(y - b) <= 1)) continue;
        if (ctx.roll('chimney', x, y) % (look.chimney_every ?? 6) !== 0) continue;
        if (![[-1, 0], [1, 0], [0, -1], [0, 1]].every(([dx, dy]) => drawn(x + dx!, y + dy!))) continue;
        placed.push([x, y]);
        const p = pickOf(chimneys, 'ch', x, y);
        out.standing.push({ kind: 'piece', at: { x, y }, x: x * C, y: y * C - rise, w: p.w, h: p.h, foot: after(y), frame: p.frame });
      }
    }
    if (cren) {
      for (let y = Y0; y <= Math.min(h - 2, Y1); y++) for (let x = X0; x <= X1; x++) {
        if (!drawn(x, y) || door(x, y) || !drawn(x, y + 1)) continue;
        const merlon = (piece: string, px: number, py: number): void => {
          const p = get(piece)[0];
          if (p && p.px.some((v, i) => i % 4 === 3 && v > 0)) out.standing.push({ kind: 'piece', at: { x, y }, x: px, y: py, w: p.w, h: p.h, foot: after(y), frame: p.frame });
        };
        if (x > 0 && !drawn(x - 1, y)) merlon(cren.w, x * C, y * C - rise);
        if (x < w - 1 && !drawn(x + 1, y)) merlon(cren.e, x * C, y * C - rise);
        if (!drawn(x, y - 1)) merlon(cren.n, x * C, y * C - rise - cren.n_rise);
      }
    }
    const turrets = get('big_turret');
    if (turrets.length && look.turret_every) {
      const placed: Array<[number, number]> = [];
      for (let y = Y0; y <= Math.min(h - 2, Y1); y++) for (let x = X0; x <= X1; x++) {
        if (!(drawn(x, y) && drawn(x, y + 1)) || door(x, y)) continue;
        const corner = [x - 1, x + 1].some((a) => sq.on(a, y) && drawn(a, y) && !drawn(a, y + 1));
        if (!corner || ctx.roll('turret', x, y) % look.turret_every !== 0) continue;
        if (!placed.every(([a, b]) => Math.abs(x - a) + Math.abs(y - b) >= (look.turret_apart ?? 5))) continue;
        placed.push([x, y]);
        const t = pickOf(turrets, 'tu', x, y);
        out.standing.push({ kind: 'big', at: { x, y }, x: x * C + C / 2 - t.w / 2, y: y * C + C - t.h - 6, w: t.w, h: t.h, foot: after(y), frame: t.frame });
      }
    }
  };

  const coats = look.coats;
  if (coats && coats.names.length > 1) {
    // runs of a block in their own colours (the docks, the towns): each drawn alone, so a line divides their roofs
    for (const [coat, cells] of coatGroups(ctx, coats, (x, y) => (wallAt(x, y) && !rock(x, y)) || doorAt(x, y))) {
      let x0 = w, y0 = h, x1 = 0, y1 = 0;
      for (const c of cells) { const x = c % w, y = Math.floor(c / w); x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
      draw((x, y) => cells.has(y * w + x), coated(ctx.all.bind(ctx), coat), { x0, y0, x1, y1 });
    }
  } else {
    draw(() => true, ctx.all.bind(ctx));
  }

  // one image per square, sorted with the units at the square's middle
  for (const [y, row] of rows) {
    for (const [x, piece] of row.cells) {
      if (piece.empty()) continue;
      out.standing.push({ kind: 'wall', at: { x, y }, x: x * C, y: rowTop(y), w: C, h: rowH, foot: y * C + C / 2, px: piece.px });
    }
  }
  return out;
}

/**
 * A row of raised wall squares, each its own small canvas: everything a wall square draws stays in its own
 * column, so a row is drawn as the tool draws it (in row coordinates) without a board-wide canvas per row.
 */
class RowCells {
  readonly cells = new Map<number, Canvas>();
  constructor(private readonly h: number) {}
  private cell(x: number): Canvas { let c = this.cells.get(x); if (!c) { c = new Canvas(C, this.h); this.cells.set(x, c); } return c; }
  draw(p: Piece, X: number, Y: number): void { this.cell(Math.floor(X / C)).draw(p, X % C, Y); }
  set(X: number, Y: number, c: RGBA): void { if (X >= 0) this.cell(Math.floor(X / C)).set(X % C, Y, c); }
  get(X: number, Y: number): RGBA { return this.cell(Math.floor(X / C)).get(X % C, Y); }
}

/** (coat, squares) of every run: each block of walls and doors cut across into runs `span` wide, seeded, no two runs side by side in one coat. */
function coatGroups(ctx: Ctx, coats: { names: string[]; span: number[] }, wall: (x: number, y: number) => boolean): Array<[string, Set<number>]> {
  const { w, h } = ctx.sq;
  const runs: Array<[string, Set<number>, number]> = [];
  const seen = new Set<number>();
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const start = y * w + x;
    if (seen.has(start) || !wall(x, y)) continue;
    const block = new Set<number>(), todo = [[x, y] as const];
    while (todo.length) {
      const [a, b] = todo.pop()!;
      if (a < 0 || b < 0 || a >= w || b >= h || block.has(b * w + a) || !wall(a, b)) continue;
      block.add(b * w + a);
      todo.push([a + 1, b], [a - 1, b], [a, b + 1], [a, b - 1]);
    }
    for (const c of block) seen.add(c);
    const xs = [...block].map((c) => c % w);
    let x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    let last: string | undefined;
    while (x0 <= x1) {
      const span = coats.span[ctx.roll('coat span', x, y + x0 * 1000) % coats.span.length]!;
      const names = coats.names.filter((c) => c !== last);
      const coat = names[ctx.roll('coat', x, y + x0 * 1000) % names.length]!;
      const cells = new Set([...block].filter((c) => c % w >= x0 && c % w < x0 + span));
      runs.push([coat, cells, Math.min(...[...cells].map((c) => Math.floor(c / w)))]);
      x0 += span; last = coat;
    }
  }
  return runs.sort((a, b) => a[2] - b[2]).map(([c, s]) => [c, s]);
}
